import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import WineCard from "@/components/WineCard";
import { WINES, WINE_COLORS, winesByColor } from "@/data/wines";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Extract individual grape names from a "grape" field like "80% Negroamaro · 20% Malvasia Nera"
const parseGrapes = (raw) =>
  raw
    .split("·")
    .map((part) => part.replace(/\d+\s*%/g, "").trim())
    .filter(Boolean);

// Unique sorted list of grape names across all wines
const ALL_GRAPES = Array.from(
  new Set(WINES.flatMap((w) => parseGrapes(w.grape)))
).sort((a, b) => a.localeCompare(b, "fr"));

const Catalog = () => {
  const [params, setParams] = useSearchParams();
  const colorParam = params.get("couleur") || "all";
  const grapeParam = params.get("cepage") || "all";
  const [sort, setSort] = useState("name-asc");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [colorParam, grapeParam]);

  const setColor = (c) => {
    const next = new URLSearchParams(params);
    if (c === "all") next.delete("couleur");
    else next.set("couleur", c);
    setParams(next, { replace: true });
  };

  const setGrape = (g) => {
    const next = new URLSearchParams(params);
    if (!g || g === "all") next.delete("cepage");
    else next.set("cepage", g);
    setParams(next, { replace: true });
  };

  const wines = useMemo(() => {
    let list = colorParam === "all" ? WINES : WINES.filter((w) => w.color === colorParam);
    if (grapeParam !== "all") {
      list = list.filter((w) =>
        parseGrapes(w.grape).some(
          (g) => g.toLowerCase() === grapeParam.toLowerCase()
        )
      );
    }
    const sorted = [...list];
    if (sort === "name-asc") sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "price-asc") sorted.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") sorted.sort((a, b) => b.price - a.price);
    return sorted;
  }, [colorParam, grapeParam, sort]);

  const colorMeta = colorParam === "all" ? null : WINE_COLORS[colorParam];
  const title = colorMeta ? `Nos vins ${colorMeta.label.toLowerCase()}` : "Toute la cave";

  const filterOptions = [
    { id: "all", label: "Toutes", count: WINES.length },
    ...Object.values(WINE_COLORS).map((c) => ({
      id: c.id,
      label: c.label,
      count: winesByColor(c.id).length,
    })),
  ];

  // Available grape options, scoped to the currently selected color so chips reflect the visible cellar.
  const grapeOptions = useMemo(() => {
    const scope = colorParam === "all" ? WINES : WINES.filter((w) => w.color === colorParam);
    const set = new Set(scope.flatMap((w) => parseGrapes(w.grape)));
    return ALL_GRAPES.filter((g) => set.has(g));
  }, [colorParam]);

  // If the active grape no longer applies to the new color scope, clear it gently.
  useEffect(() => {
    if (grapeParam !== "all" && !grapeOptions.includes(grapeParam)) {
      setGrape("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorParam]);

  return (
    <div data-testid="catalog-page" className="bg-[#F9F6F0]">
      <section className="border-b border-[#E8E2D2] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 md:py-20">
          <nav className="flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#5C5651] mb-5">
            <Link to="/" className="hover:text-[#5B2333]">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A1617]">{title}</span>
          </nav>
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold">Catalogue</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-[#1A1617] mt-2 sm:mt-3 leading-tight">
            {colorMeta ? (
              <>Vins <em className="italic" style={{ color: colorMeta.accent }}>{colorMeta.label.toLowerCase()}</em></>
            ) : (
              <>Toute la <em className="italic text-[#5B2333]">cave</em></>
            )}
          </h1>
          <p className="text-sm sm:text-base text-[#5C5651] mt-3 sm:mt-4 max-w-2xl">
            {colorMeta?.id === "red" && "Charpentés, élégants, généreux comme le soleil du Salento."}
            {colorMeta?.id === "white" && "Frais, aromatiques, équilibrés. Sélection à venir prochainement."}
            {colorMeta?.id === "rose" && "Frais, fruités, gourmands. Sélection à venir prochainement."}
            {!colorMeta && "Découvrez l'intégralité de notre sélection artisanale des Pouilles."}
          </p>
        </div>
      </section>

      <section className="py-8 sm:py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-3">Catégories</p>
            <ul className="flex lg:flex-col gap-2 lg:gap-0.5 overflow-x-auto -mx-4 px-4 lg:overflow-visible lg:mx-0 lg:px-0" data-testid="color-filters">
              {filterOptions.map((c) => {
                const active = colorParam === c.id;
                const disabled = c.id !== "all" && c.count === 0;
                return (
                  <li key={c.id} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => setColor(c.id)}
                      data-testid={`filter-color-${c.id}`}
                      className={`text-left text-sm py-2 px-3 lg:px-0 lg:py-1.5 transition-colors w-full whitespace-nowrap flex items-center justify-between gap-3 ${
                        active
                          ? "text-[#5B2333] font-medium lg:border-l-2 lg:pl-3 lg:border-[#5B2333]"
                          : "text-[#1A1617] hover:text-[#5B2333] lg:pl-3 lg:border-l-2 lg:border-transparent"
                      }`}
                    >
                      <span>{c.label}</span>
                      <span className={`text-[10px] ${disabled ? "text-[#5C5651]/50 italic" : "text-[#5C5651]"}`}>
                        {disabled ? "à venir" : c.count}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Cépage filter — pill chips, scoped to current color */}
            <div className="mt-8 lg:mt-10">
              <div className="flex items-baseline justify-between mb-3">
                <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold">Cépage</p>
                {grapeParam !== "all" && (
                  <button
                    type="button"
                    onClick={() => setGrape("all")}
                    data-testid="clear-grape-filter"
                    className="text-[10px] tracking-[0.15em] uppercase text-[#5C5651] hover:text-[#5B2333] flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Effacer
                  </button>
                )}
              </div>
              <div
                data-testid="grape-filters"
                className="flex flex-wrap gap-2"
              >
                {grapeOptions.map((g) => {
                  const active = grapeParam === g;
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGrape(active ? "all" : g)}
                      data-testid={`filter-grape-${g.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`text-[11px] tracking-[0.1em] px-3 py-1.5 border transition-colors ${
                        active
                          ? "bg-[#5B2333] text-white border-[#5B2333]"
                          : "bg-white text-[#1A1617] border-[#E8E2D2] hover:border-[#5B2333]/60 hover:text-[#5B2333]"
                      }`}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mt-8 lg:mt-10 mb-3">Trier par</p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                data-testid="sort-trigger"
                className="bg-white border border-[#E8E2D2] rounded-none focus:ring-[#5B2333]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="name-asc" data-testid="sort-name-asc">Nom (A → Z)</SelectItem>
                <SelectItem value="price-asc" data-testid="sort-price-asc">Prix croissant</SelectItem>
                <SelectItem value="price-desc" data-testid="sort-price-desc">Prix décroissant</SelectItem>
              </SelectContent>
            </Select>
          </aside>

          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-5">
              <p data-testid="catalog-count" className="text-xs sm:text-sm text-[#5C5651]">
                {wines.length} cuvée{wines.length > 1 ? "s" : ""}
                {grapeParam !== "all" && (
                  <span className="ml-2 text-[#5B2333]">· {grapeParam}</span>
                )}
              </p>
            </div>
            {wines.length === 0 ? (
              <div className="border border-[#E8E2D2] bg-white p-12 sm:p-16 text-center" data-testid="catalog-empty">
                <p className="font-heading text-2xl sm:text-3xl text-[#1A1617]">
                  {grapeParam !== "all" ? "Aucune cuvée" : "Bientôt disponibles"}
                </p>
                <p className="text-sm text-[#5C5651] mt-2">
                  {grapeParam !== "all"
                    ? `Aucun vin avec le cépage « ${grapeParam} » dans cette sélection.`
                    : "Cette catégorie sera proposée prochainement. Découvrez nos vins rouges en attendant."}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setGrape("all");
                    if (colorParam !== "red" && grapeParam === "all") setColor("red");
                  }}
                  data-testid="catalog-empty-cta"
                  className="inline-flex items-center gap-2 mt-6 bg-[#5B2333] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#4A1525] transition-colors"
                >
                  {grapeParam !== "all" ? "Réinitialiser le cépage" : "Voir les rouges"}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 lg:gap-6" data-testid="catalog-grid">
                {wines.map((w) => (
                  <WineCard key={w.id} wine={w} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Catalog;
